import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface StreamingTextProps {
    text: string;
    speed?: number;
    shouldStream?: boolean;
}

const StreamingText: React.FC<StreamingTextProps> = ({
    text,
    speed = 10,
    shouldStream = true,
}) => {
    const initialText = shouldStream ? '' : text;
    const [displayedText, setDisplayedText] = useState(initialText);
    const animationFrameRef = useRef<number | undefined>(undefined);
    const lastUpdateTimeRef = useRef(0);
    const targetTextRef = useRef(text);
    const displayedTextRef = useRef(initialText);

    // Render markdown with some sensible defaults for tables and lists
    const markdownComponents = useMemo(
        () => ({
            table: (props: React.HTMLAttributes<HTMLTableElement>) => (
                <div className="my-3 w-full overflow-x-auto">
                    <table
                        className="min-w-[480px] border-collapse text-sm"
                        {...props}
                    />
                </div>
            ),
            thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
                <thead className="bg-gray-50" {...props} />
            ),
            tbody: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
                <tbody {...props} />
            ),
            tr: (props: React.HTMLAttributes<HTMLTableRowElement>) => (
                <tr className="border-b last:border-b-0" {...props} />
            ),
            th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
                <th
                    className="border px-3 py-2 text-left font-semibold"
                    {...props}
                />
            ),
            td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
                <td className="border px-3 py-2 align-top" {...props} />
            ),
            ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
                <ul className="my-2 list-disc space-y-1 pl-5" {...props} />
            ),
            ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
                <ol className="my-2 list-decimal space-y-1 pl-5" {...props} />
            ),
            li: (props: React.LiHTMLAttributes<HTMLLIElement>) => (
                <li className="leading-relaxed" {...props} />
            ),
            p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
                <p className="my-2 leading-relaxed" {...props} />
            ),
            code: (props: React.HTMLAttributes<HTMLElement>) => (
                <code
                    className="rounded bg-gray-100 px-1 py-[1px] text-[13px]"
                    {...props}
                />
            ),
            pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
                <pre
                    className="my-3 overflow-x-auto rounded-lg bg-gray-900 px-3 py-2 text-[13px] text-gray-50"
                    {...props}
                />
            ),
        }),
        [],
    );

    useEffect(() => {
        targetTextRef.current = text;

        // Always clear any in-flight animation before deciding what to do next
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = undefined;
        }

        // When not streaming (e.g. returning to the page), render immediately
        if (!shouldStream) {
            displayedTextRef.current = text;
            setDisplayedText(text);
            return;
        }

        const start = () => {
            lastUpdateTimeRef.current = performance.now();

            const step = (currentTime: number) => {
                const target = targetTextRef.current;
                const current = displayedTextRef.current;

                if (current === target) {
                    animationFrameRef.current = undefined;
                    return;
                }

                const elapsed = currentTime - lastUpdateTimeRef.current;
                if (elapsed >= speed) {
                    const increment = Math.max(1, Math.floor(elapsed / speed));
                    const nextLength = Math.min(
                        target.length,
                        current.length + increment,
                    );
                    const nextText = target.slice(0, nextLength);
                    displayedTextRef.current = nextText;
                    setDisplayedText(nextText);
                    lastUpdateTimeRef.current = currentTime;
                }

                animationFrameRef.current = requestAnimationFrame(step);
            };

            animationFrameRef.current = requestAnimationFrame(step);
        };

        // If new text is shorter or equal, snap immediately; otherwise animate the diff
        if (text.length <= displayedTextRef.current.length) {
            displayedTextRef.current = text;
            setDisplayedText(text);
        } else {
            start();
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [text, speed, shouldStream]);

    return (
        <div className="text-sm leading-relaxed break-words">
            <ReactMarkdown
                components={markdownComponents}
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
            >
                {displayedText}
            </ReactMarkdown>
        </div>
    );
};

export default StreamingText;
